import i18n from '@app/i18n';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// "2024-04-30" -> "Apr '24"
export const shortPeriod = (date: string): string => {
  const [year, month] = date.split('-').map(Number);
  return `${MONTHS[month - 1]} '${String(year).slice(2)}`;
};

// "2026-07-30" -> "30 Jul", for a daily axis where a month-level label would repeat. Parsed from the
// string rather than via `new Date`, which reads a bare yyyy-mm-dd as UTC midnight and then renders the
// previous day for any viewer west of UTC.
export const shortDay = (date: string): string => {
  const [, month, day] = date.split('-').map(Number);
  return `${day} ${MONTHS[month - 1]}`;
};

// Whole months from one date to the next, floored at zero. A backport landing on an old major can be
// published AFTER the current release, which would otherwise come out negative.
export const monthsBetween = (from: string, to: string): number => {
  const [fy, fm] = from.split('-').map(Number);
  const [ty, tm] = to.split('-').map(Number);
  return Math.max(0, (ty - fy) * 12 + (tm - fm));
};

// A month count as a human duration: "8 months", "1 year & 6 months", "2 years" (localised).
export const formatMonths = (total: number): string => {
  if (total < 12) return i18n.t('productDashboard.durationMonths', { count: total });
  const years = Math.floor(total / 12);
  const months = total % 12;
  const yearsLabel = i18n.t('productDashboard.durationYears', { count: years });
  if (months === 0) return yearsLabel;
  return i18n.t('productDashboard.durationYearsMonths', {
    years: yearsLabel,
    months: i18n.t('productDashboard.durationMonths', { count: months }),
  });
};

// "8 months", "1 year & 6 months", "2 years"
export const durationLabel = (from: string, to: string): string => formatMonths(monthsBetween(from, to));

const DAY_MS = 24 * 60 * 60 * 1000;

const timeIntoMonth = (date: Date): number => (date.getUTCDate() - 1) * DAY_MS + (date.getTime() % DAY_MS);

// Calendar months, counted only once the day and time of the month come round, so 30 Sep to 13 Oct is not a month.
export const wholeMonthsSince = (from: string, now: Date): number => {
  const start = new Date(from);
  const months =
    (now.getUTCFullYear() - start.getUTCFullYear()) * 12 +
    (now.getUTCMonth() - start.getUTCMonth()) -
    (timeIntoMonth(now) < timeIntoMonth(start) ? 1 : 0);
  return Math.max(0, months);
};

// "9 days" below a month, then "8 months", "1 year & 6 months", "2 years" (localised).
export const durationSince = (from: string, now: Date): string => {
  const months = wholeMonthsSince(from, now);
  if (months >= 1) return formatMonths(months);
  const days = Math.floor((now.getTime() - new Date(from).getTime()) / DAY_MS);
  return days < 1
    ? i18n.t('productDashboard.durationLessThanDay')
    : i18n.t('productDashboard.durationDays', { count: days });
};

const parseVersion = (version: string): { core: number[]; prerelease: string } | null => {
  const match = /^v?(\d+)\.(\d+)\.(\d+)(?:-([0-9A-Za-z.-]+))?/.exec(version.trim());
  return match ? { core: [Number(match[1]), Number(match[2]), Number(match[3])], prerelease: match[4] ?? '' } : null;
};

const compareIdentifiers = (a: string, b: string): number => {
  const [aNumeric, bNumeric] = [/^\d+$/.test(a), /^\d+$/.test(b)];
  if (aNumeric && bNumeric) return Number(a) - Number(b);
  if (aNumeric !== bNumeric) return aNumeric ? -1 : 1;
  // ASCII order, as semver specifies and the API's semver.ts does.
  if (a === b) return 0;
  return a < b ? -1 : 1;
};

/** Semver precedence: negative when `a` is older, positive when newer, 0 when equal or unparseable. */
export const compareVersions = (a: string, b: string): number => {
  const [left, right] = [parseVersion(a), parseVersion(b)];
  if (!left || !right) return 0;
  const core = left.core.map((part, index) => part - right.core[index]).find(diff => diff !== 0);
  if (core) return core;
  // A release outranks any prerelease of the same version.
  if (!left.prerelease || !right.prerelease) return Number(!left.prerelease) - Number(!right.prerelease);
  const [leftIds, rightIds] = [left.prerelease.split('.'), right.prerelease.split('.')];
  const order = leftIds.map((id, index) => (index < rightIds.length ? compareIdentifiers(id, rightIds[index]) : 0));
  return order.find(diff => diff !== 0) ?? leftIds.length - rightIds.length;
};

export const isPrerelease = (version: string): boolean => !!parseVersion(version)?.prerelease;

/** Major, minor and patch, or null when the version does not parse. */
export const versionCore = (version: string): number[] | null => parseVersion(version)?.core ?? null;

/** Same release by semver precedence, so "v1.2.3" and "1.2.3+build.5" match "1.2.3". */
export const isSameVersion = (a: string, b: string): boolean =>
  a === b || (!!parseVersion(a) && !!parseVersion(b) && compareVersions(a, b) === 0);

/** The release line, as npm's caret ranges see it: "18.x", or for 0.x the first non-zero segment ("0.48.x", "0.0.3"). */
export const versionLineLabel = (version: string): string | null => {
  const parsed = parseVersion(version);
  if (!parsed) return null;
  const [major, minor, patch] = parsed.core;
  if (major > 0) return `${major}.x`;
  return minor > 0 ? `0.${minor}.x` : `0.0.${patch}`;
};
